import { all, call, put, takeEvery, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { uploadFailed, garmentPlacementOutsideBounds, garmentPlacementFailed } from 'src/components/alert/slice';
import { getRegionSetting } from 'src/selectors';
import { toSafeUploadName, uploadImageAndGetUrl } from 'src/util/upload';
import { cleanTexture } from 'src/services/sceneClient';
import { logError } from 'src/loggingManager';
import {
    getCylinderWarpById,
    getSceneSize,
    getSceneDefiningBackgroundId,
    getBackgroundById,
    getDocumentById,
} from 'src/selectors/layers';
import { getTransformsById } from 'src/selectors/transforms';
import { getDecorationSpecs, getSurfaceByPage } from 'src/selectors/pages/editor';
import { PERSPECTIVE_TRANSFORM, TUBE_TRANSFORM, PLACEMENT_TRANSFORM } from 'src/models/transforms';
import { CYLINDER_WARP, GARMENT_WARP } from 'src/models/documentWarps';
import { computeAutoPosition, DEFAULT_SCALE_AXIS, type AutoPositionFailureReason } from 'src/util/autoPosition';
import { defaultMarkers } from 'src/pages/editor/components/canvas/garmentMarkers/defaultMarkers';
import type { GarmentPlacementInputType } from 'src/types/decorationSpecs';
import {
    removeMask,
    uploadMask,
    maskUploaded,
    setWarp,
    replaceTransforms,
    updateCylinder,
    toggleFullWrap,
    updatePrecisionWrap,
    rotateCylinder,
    textureUploaded,
    removeTexture,
    uploadTexture,
    recomputeGarmentPlacement,
} from './slice';
import { requestGarmentPlacementRecompute } from './garmentPlacement/actions';
import { parseCylinderWarp, processTubeWarp } from 'src/util/cylinderWarp/tubeWarpTransform';

type CylinderWarpParams = {
    cylinderTop: {
        center: { x: number; y: number };
        radiusX: number;
        radiusY: number;
        rotationDegrees: number;
    };
    cylinderBottom: {
        center: { x: number; y: number };
        radiusX: number;
        radiusY: number;
        rotationDegrees: number;
    };
    decorationAreaDimensions: { width: number; height: number };
    productDiameter: number;
    decorationExtentDegreesAlpha: number;
    decorationExtentDegreesBeta: number;
    isDecorationRotated: boolean;
    imageDimensions: { width: number; height: number };
};

function createCylinderWarpParameters(width: number, height: number): CylinderWarpParams {
    const middleX = width / 2;
    const topY = height * 0.3;
    const bottomY = height * 0.7;
    const ellipseWidth = width * 0.2;
    const ellipseHeight = height * 0.05;

    const productDiameter = width * 0.5;
    const decorationAreaSize = (productDiameter * Math.PI) / 4;
    return {
        cylinderTop: {
            center: { x: middleX, y: topY },
            radiusX: ellipseWidth,
            radiusY: ellipseHeight,
            rotationDegrees: 0.0,
        },
        cylinderBottom: {
            center: { x: middleX, y: bottomY },
            radiusX: ellipseWidth,
            radiusY: ellipseHeight,
            rotationDegrees: 0.0,
        },
        decorationAreaDimensions: {
            width: decorationAreaSize,
            height: decorationAreaSize,
        },
        productDiameter,
        decorationExtentDegreesAlpha: 225,
        decorationExtentDegreesBeta: 90,
        isDecorationRotated: false,
        imageDimensions: { width, height },
    };
}

function* uploadAndAddMask({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    const region = yield* select(getRegionSetting);
    const sceneSize = yield* select(getSceneSize);
    try {
        const url = (yield* call(uploadImageAndGetUrl as any, file, region, sceneSize)) as string;
        yield* put(maskUploaded({ url, id, fileName: toSafeUploadName(file.name) }));
    } catch (error: any) {
        logError(
            `unable to upload mask, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(uploadFailed(`Mask: ${error}`));
        yield* put(removeMask(id));
    }
}

function* uploadAndAddTexture({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    const region = yield* select(getRegionSetting);
    try {
        const cleanedTexture = yield* call(cleanTexture, { texture: file });
        const url = yield* call(uploadImageAndGetUrl, cleanedTexture, region);
        yield* put(textureUploaded({ url, id, fileName: file.name }));
    } catch (error: any) {
        logError(
            `unable to upload texture, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(uploadFailed(`Texture: ${error}`));
        yield* put(removeTexture(id));
    }
}

function* uploadCylinderWarp(id: string): Generator<any, void, any> {
    const warp = yield* select(getCylinderWarpById, id);
    const documentTransforms = yield* select(getTransformsById, id);
    const perspectiveId = documentTransforms.find((transform: any) => transform.type === PERSPECTIVE_TRANSFORM)!.id;
    const cylinderId = documentTransforms.find((transform: any) => transform.type === TUBE_TRANSFORM)!.id;
    const placement = documentTransforms.find((transform: any) => transform.type === PLACEMENT_TRANSFORM);
    const placementId = placement ? placement.id : null;

    // TODO: Review this line of code
    const response: any = (parseCylinderWarp as any)(
        (processTubeWarp as any)(warp, id, perspectiveId, cylinderId, placementId),
    );

    if (response.perspectiveWarp && response.tubeWarp && response.documentSize) {
        const transforms = response.placementWarp
            ? [response.perspectiveWarp, response.tubeWarp, response.placementWarp]
            : [response.perspectiveWarp, response.tubeWarp];
        yield* put(
            replaceTransforms({
                id,
                transforms,
                width: response.documentSize.width,
                height: response.documentSize.height,
            }),
        );
    }
}

function* createCylinderWarp({
    payload,
}: PayloadAction<{ id: string; width: number; height: number; warpType: string }>): Generator<any, void, any> {
    const { id, width, height, warpType } = payload;
    if (warpType === CYLINDER_WARP) {
        const cylinder = createCylinderWarpParameters(width, height);
        yield* put(updateCylinder({ id, cylinder }));
    }
}

function* uploadPrecisionWrap({
    payload,
}: PayloadAction<{ id: string; width: number; diameter: number }>): Generator<any, void, any> {
    const { id, width, diameter } = payload;
    if (width > 0 && diameter > 0) {
        yield* call(uploadCylinderWarp, id);
    }
}

function* uploadFromId({ payload }: PayloadAction<string | { id: string }>): Generator<any, void, any> {
    const id = typeof payload === 'string' ? payload : payload.id;
    yield* call(uploadCylinderWarp, id);
}

/** Maps `computeAutoPosition`'s structured failure reasons to a message a designer can act on —
 * these can occur even when `getGarmentWarpAvailability` allowed the action (it checks that a
 * size chart exists at all, not that the specific body-length/width attribute it needs is present
 * within it), so this is the last line of defense against a silent no-op. */
function describeFailureReason(reason: AutoPositionFailureReason): string {
    switch (reason) {
        case 'no-panel-data':
            return 'No decoration-placement data is available for this surface.';
        case 'no-body-width':
            return "The size chart doesn't have a body width measurement for the current size.";
        case 'no-body-length':
            return "The size chart doesn't have a body length measurement for the current size.";
        case 'invalid-body-width':
            return 'The size chart body width measurement is invalid.';
        case 'invalid-body-length':
            return 'The size chart body length measurement is invalid.';
        case 'invalid-markers':
            return 'The garment markers have zero distance on the measurement axis.';
        default:
            return 'Required apparel positioning data is missing.';
    }
}

/**
 * Runs `computeAutoPosition` for a `GARMENT_WARP` document and applies the result — shared by
 * both initial entry into the warp mode and explicit "Recompute Placement" clicks.
 * Early validation failures dispatch a user-visible alert to explain missing inputs.
 *
 * If the background does not yet have calibrated landmarks, this falls back to `defaultMarkers`
 * so the initial auto-placement works right away and can be fine-tuned afterward.
 */
function* computeAndApplyGarmentPlacement(
    id: string,
    garmentPlacement: GarmentPlacementInputType,
): Generator<any, void, any> {
    const { backgroundId, panelName, scaleAxis } = garmentPlacement;
    const background = yield* select(getBackgroundById, backgroundId);
    if (!background?.width || !background.height) {
        logError(`Unable to compute garment auto-placement for document ${id}: no scene-defining background`);
        yield* put(garmentPlacementFailed('No background has been uploaded yet.'));
        return;
    }
    const markers = background.garmentMarkers ?? defaultMarkers({ width: background.width, height: background.height });

    const { decorationPlacement, bodyWidthMm, bodyLengthMm } = yield* select(getDecorationSpecs);
    const panel = decorationPlacement?.find((entry) => entry.panelName === panelName) ?? null;

    const result = computeAutoPosition({
        markers,
        panel,
        bodyWidthMm,
        bodyLengthMm,
        scaleAxis,
    });

    if (!result.ok) {
        logError(`Unable to compute garment auto-placement for document ${id}: ${result.reason}`);
        yield* put(garmentPlacementFailed(describeFailureReason(result.reason)));
        return;
    }

    // Convert background pixel coordinates into scene transform space. A no-op in the normal
    // single-background case (where this background is the scene-defining one), but scales
    // appropriately if a secondary background has different native dimensions.
    const sceneSize = yield* select(getSceneSize);
    const ratio = sceneSize.width && background.width ? sceneSize.width / background.width : 1;

    const points = [
        { x: result.x * ratio, y: result.y * ratio },
        { x: (result.x + result.width) * ratio, y: (result.y + result.height) * ratio },
    ];

    if (result.outsideGarmentBounds) {
        yield* put(garmentPlacementOutsideBounds());
    }

    yield* put(recomputeGarmentPlacement({ id, garmentPlacement, points }));
}

export function* enterGarmentWarp({
    payload,
}: PayloadAction<{ id: string; warpType: string }>): Generator<any, void, any> {
    const { id, warpType } = payload;
    if (warpType !== GARMENT_WARP) {
        return;
    }

    const doc = yield* select(getDocumentById, id);
    const surface = doc?.page ? yield* select(getSurfaceByPage, doc.page) : undefined;
    const backgroundId = yield* select(getSceneDefiningBackgroundId);

    if (!surface?.name) {
        logError(`Unable to enter GARMENT_WARP for document ${id}: no surface for page ${doc?.page}`);
        yield* put(garmentPlacementFailed("This document isn't on a recognized surface/page."));
        return;
    }
    if (!backgroundId) {
        logError(`Unable to enter GARMENT_WARP for document ${id}: no scene-defining background`);
        yield* put(garmentPlacementFailed('No background has been uploaded yet.'));
        return;
    }

    const garmentPlacement: GarmentPlacementInputType = {
        backgroundId,
        panelName: surface.panelName ?? surface.name,
        scaleAxis: DEFAULT_SCALE_AXIS,
    };

    yield* call(computeAndApplyGarmentPlacement, id, garmentPlacement);
}

export function* recomputeGarmentPlacementFromRequest({
    payload,
}: PayloadAction<{ id: string }>): Generator<any, void, any> {
    const { id } = payload;
    const doc = yield* select(getDocumentById, id);
    if (!doc?.garmentPlacement) {
        logError(`Unable to recompute garment placement for document ${id}: not in GARMENT_WARP mode`);
        yield* put(
            garmentPlacementFailed('This document is not in garment auto-placement mode — select "Auto-Place" first.'),
        );
        return;
    }

    yield* call(computeAndApplyGarmentPlacement, id, doc.garmentPlacement);
}

export default function* documentsSaga() {
    return yield* all([
        yield* takeEvery(uploadMask, uploadAndAddMask),
        yield* takeEvery(setWarp, createCylinderWarp),
        yield* takeEvery(updateCylinder, uploadFromId),
        yield* takeEvery(toggleFullWrap, uploadFromId),
        yield* takeEvery(rotateCylinder, uploadFromId),
        yield* takeEvery(updatePrecisionWrap, uploadPrecisionWrap),
        yield* takeEvery(uploadTexture, uploadAndAddTexture),
        yield* takeEvery(setWarp, enterGarmentWarp),
        yield* takeEvery(requestGarmentPlacementRecompute, recomputeGarmentPlacementFromRequest),
    ]);
}
